/**
 * SoloExplorerProfileNudge
 *
 * Dismissible bottom-right card that reminds Solo Explorer users to complete
 * their profile. The backend decides the stage + reward amount based on
 * days-since-signup:
 *   · Day 0-2  → "welcome"  · +50 Stars  · cooldown 24h
 *   · Day 3-6  → "reminder" · +50 Stars  · cooldown 48h
 *   · Day 7+   → "final"    · +100 Stars + Profile Pioneer badge · cooldown 72h
 *
 * UX note (Feb 2026): This card no longer embeds its own mini-form. It is a
 * pure CTA that routes the user to the unified `/profile/setup` page, where
 * the full profile (age band, interests, goals, etc.) is captured in one
 * place. The +Stars bonus is awarded by /api/profile/setup on save.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Sparkles, Star, X, ChevronRight, Award } from "lucide-react";
import { useApp, API } from "../context";

// Cooldowns per stage, higher stages wait longer between re-appearances so
// we don't pester power users who just keep dismissing.
const COOLDOWN_MS = {
  welcome: 24 * 60 * 60 * 1000,   // 1 day
  reminder: 48 * 60 * 60 * 1000,  // 2 days
  final: 72 * 60 * 60 * 1000,     // 3 days
};
const DISMISS_KEY = "isaiah_solo_nudge_dismissed_at";

const STAGE_COPY = {
  welcome: {
    title: "Finish your profile",
    pitch: "Tell us your age & interests so lessons fit you perfectly. Takes about 30 seconds.",
    badgeText: null,
  },
  reminder: {
    title: "Still with us? Finish your profile",
    pitch: "Just a few taps to unlock content that actually matches your age & interests. We'll even bump your Stars.",
    badgeText: null,
  },
  final: {
    title: "One-time founder bonus",
    pitch: "Complete your profile today and you'll earn a DOUBLE reward + the exclusive Profile Pioneer badge. Limited to original explorers.",
    badgeText: "Profile Pioneer badge",
  },
};

const SoloExplorerProfileNudge = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [state, setState] = useState(null);          // { visible, stage, reward_stars, reward_badge }
  const [visible, setVisible] = useState(false);

  // Ask the server which stage this user is in. Single source of truth.
  useEffect(() => {
    if (!user) return;
    if ((user.account_type || "").toLowerCase() !== "solo_explorer") return;
    if (user.profile_setup_complete === true) return;
    let cancelled = false;
    axios.get(`${API}/auth/profile/solo-nudge-state`, { withCredentials: true })
      .then(r => { if (!cancelled) setState(r.data); })
      .catch(() => { if (!cancelled) setState({ visible: false }); });
    return () => { cancelled = true; };
  }, [user]);

  // Respect the per-stage cooldown before actually showing the card.
  useEffect(() => {
    if (!state?.visible) return;
    const cooldown = COOLDOWN_MS[state.stage] || COOLDOWN_MS.welcome;
    const lastByStage = JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}");
    const last = Number(lastByStage[state.stage] || 0);
    if (Date.now() - last < cooldown) return;
    const t = setTimeout(() => setVisible(true), 3500);
    return () => clearTimeout(t);
  }, [state]);

  // Listen for a force-show event dispatched from e.g. the header avatar-menu
  // "Claim your X Stars" pill. Bypasses the cooldown so the user can always
  // reach the profile form from the dropdown, but only when there IS an
  // unclaimed bonus (state.visible === true).
  useEffect(() => {
    const handler = () => {
      if (state?.visible) setVisible(true);
    };
    window.addEventListener("isaiah:show-solo-nudge", handler);
    return () => window.removeEventListener("isaiah:show-solo-nudge", handler);
  }, [state]);

  const snooze = () => {
    if (state?.stage) {
      const cur = JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}");
      cur[state.stage] = Date.now();
      localStorage.setItem(DISMISS_KEY, JSON.stringify(cur));
    }
    setVisible(false);
  };

  const goToSetup = () => {
    // Auto-snooze so the card doesn't re-appear while the user is on the
    // setup page. If they bail out mid-setup, the cooldown still lets it
    // show up again next session.
    snooze();
    navigate("/profile/setup");
  };

  if (!visible || !user || !state?.visible) return null;
  // Hide immediately if the profile has since been completed (e.g. the
  // user just finished /profile/setup, refreshUser() updates this flag
  // even before the next solo-nudge-state fetch returns, so relying on
  // state alone leaves a stale nudge on screen for a beat).
  if (user.profile_setup_complete === true) return null;
  const stage = state.stage || "welcome";
  const copy = STAGE_COPY[stage] || STAGE_COPY.welcome;
  const reward = state.reward_stars || 50;
  const isFinal = stage === "final";

  return (
    <div
      className="fixed bottom-4 right-4 z-[9500] w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl overflow-hidden shadow-2xl animate-slide-in-right"
      style={{
        background: "linear-gradient(180deg, rgba(20,20,40,0.97), rgba(30,15,45,0.97))",
        border: `1px solid ${isFinal ? "rgba(251,191,36,0.45)" : "rgba(244,114,182,0.35)"}`,
        boxShadow: isFinal
          ? "0 0 40px rgba(251,191,36,0.28), 0 20px 40px rgba(0,0,0,0.4)"
          : "0 0 40px rgba(244,114,182,0.22), 0 20px 40px rgba(0,0,0,0.4)",
      }}
      data-testid="solo-explorer-profile-nudge"
      data-stage={stage}
    >
      {/* Header */}
      <div
        className="relative px-4 py-3 border-b border-white/10"
        style={{
          background: isFinal
            ? "linear-gradient(90deg, rgba(251,191,36,0.18), rgba(244,114,182,0.12))"
            : "linear-gradient(90deg, rgba(244,114,182,0.15), rgba(251,191,36,0.1))",
        }}
      >
        <button
          onClick={snooze}
          className="absolute top-2 right-2 w-6 h-6 rounded-full hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors"
          title="Remind me later"
          data-testid="solo-nudge-close"
        >
          <X size={14} />
        </button>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 ${
            isFinal ? "bg-amber-400/30 border-amber-300/60" : "bg-amber-400/20 border-amber-300/40"
          }`}>
            {isFinal ? <Award size={14} className="text-amber-200" /> : <Star size={14} className="text-amber-300" fill="currentColor" />}
          </div>
          <div className="flex-1 min-w-0 pr-6">
            <p className="text-white font-fredoka font-bold text-sm leading-tight">{copy.title}</p>
            <p className="text-amber-200 text-[11px] font-nunito leading-tight mt-0.5">
              Earn <strong>{reward} Stars</strong>
              {copy.badgeText ? <> + <strong>{copy.badgeText}</strong></> : <> right now</>}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4">
        <p className="text-white/70 text-xs font-nunito leading-relaxed mb-3">{copy.pitch}</p>
        <button
          onClick={goToSetup}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-white font-fredoka font-bold text-xs transition-all hover:scale-[1.02]"
          style={{
            background: isFinal
              ? "linear-gradient(135deg, #FBBF24, #F472B6)"
              : "linear-gradient(135deg, #F472B6, #FB923C)",
            boxShadow: isFinal
              ? "0 4px 16px rgba(251,191,36,0.35)"
              : "0 4px 16px rgba(244,114,182,0.3)",
          }}
          data-testid="solo-nudge-start"
        >
          <Sparkles size={13} /> Complete profile · +{reward} Stars <ChevronRight size={13} />
        </button>
        <button
          onClick={snooze}
          className="w-full text-center text-white/35 hover:text-white/60 text-[10px] font-nunito mt-2 transition-colors"
          data-testid="solo-nudge-snooze"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default SoloExplorerProfileNudge;
